// HM-348 cluster 16.1.5: Step 4 riepilogo + cascade + success v3.
//
// Auto-run cascade al mount (l'utente ha gia confermato in Step 3 via
// WizardFooter "Conferma e paga"). Stati visualizzati:
//  - running: progress card 5 step verticali con icone stato.
//  - done: success-icon CircleCheck verde + code-display-box 44px mono
//    + action-row (Ripeti WhatsApp / Stampa promemoria) + summary-rows.
//  - error: ErrorRecovery globale (renderizzato dal WalkInModal).

import { CircleCheck, MessageCircle, Printer } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { sendMessagingTemplate } from '../../api/messaging.js';
import { getCurrentTenant } from '../../api/tenant.js';
import { formatModeDynamic } from '../../lib/formatMode.js';
import { runWalkInCascade } from '../../lib/walk-in-cascade.js';
import {
  type WalkInPayment,
  modeBasePrice,
  useWalkInWizard,
  walkInEndAt,
} from '../../stores/walkIn.js';

const CASCADE_STEPS = [
  { key: 'booking', label: 'Creazione prenotazione' },
  { key: 'payment', label: 'Pagamento' },
  { key: 'fiscal', label: 'Scontrino fiscale' },
  { key: 'access_code', label: 'Codice di accesso' },
  { key: 'messaging', label: 'Invio WhatsApp' },
  // HM-355 cluster 16.1.7: confirm + check_in transition + rooms.status='occupied'.
  { key: 'confirm', label: 'Conferma prenotazione' },
  { key: 'check_in', label: 'Check-in camera' },
] as const;

export function StepRecap() {
  const wizard = useWalkInWizard();
  const { room, mode, guest, payment, cascade, isAnonymous, anonymousId, dynamicPricing } = wizard;
  const [resend, setResend] = useState<{
    status: 'idle' | 'sending' | 'sent' | 'error';
    msg?: string;
  }>({ status: 'idle' });
  const startedRef = useRef(false);

  // HM-571 multi-hotel: tenant a chiavi fisiche cambia UI step access
  // (label cascade + box success: "Consegna chiave" invece di "Codice").
  const tenantQuery = useQuery({
    queryKey: ['tenant', 'me'],
    queryFn: getCurrentTenant,
    staleTime: 5 * 60_000,
  });
  const usesPhysicalKeys = tenantQuery.data?.tenant.accessMethod === 'key';

  // Auto-run cascade al primo mount (idempotente: una sola volta per istanza modal).
  useEffect(() => {
    if (startedRef.current) return;
    if (cascade.currentStep === 'idle' && !cascade.running && !cascade.error) {
      startedRef.current = true;
      void runWalkInCascade(wizard);
    }
  }, [cascade.currentStep, cascade.running, cascade.error, wizard]);

  if (!room) return null;
  const start = new Date();
  // QA sweep 2026-07-22 (bug MEDIO): stessa durata usata dalla cascade
  // (dynamicPricing.selectedHours), non piu' il legacy mode fisso (+4h).
  // Regola 2026-07-23: pernottamento = uscita entro le 12:00 (walkInEndAt).
  const end = walkInEndAt(start, mode, dynamicPricing);
  const total = dynamicPricing.priceCalc?.total ?? modeBasePrice(mode);

  // HM-355 cluster 16.1.7: 'done' o 'done_with_warnings' = cascade
  // chiusa (booking salvato + check-in attivo). accessCode puo' essere
  // null se lo step access_code e' fallito (graceful), ma la camera
  // e' comunque occupata.
  const isDone = cascade.currentStep === 'done' || cascade.currentStep === 'done_with_warnings';

  const onResendWhatsApp = async () => {
    if (!cascade.bookingId || !cascade.accessCode) return;
    setResend({ status: 'sending' });
    try {
      const r = await sendMessagingTemplate({
        bookingId: cascade.bookingId,
        template: 'access_code_delivery',
        to: guest.phone ?? null,
        variables: {
          code: cascade.accessCode,
          room: room.code,
        },
      });
      setResend({
        status: r.status === 'skipped' ? 'error' : 'sent',
        msg: r.status === 'skipped' ? 'Numero ospite mancante: invio saltato.' : undefined,
      });
    } catch (e) {
      setResend({
        status: 'error',
        msg: e instanceof Error ? e.message : 'Errore invio',
      });
    }
  };

  const onPrint = () => {
    window.print();
  };

  return (
    <section data-testid="step-recap" className="flex flex-col gap-6">
      {isDone && cascade.warnings.length > 0 && <WarningsBanner warnings={cascade.warnings} />}
      {isDone ? (
        <SuccessView
          accessCode={cascade.accessCode ?? ''}
          roomCode={room.code}
          onResend={onResendWhatsApp}
          onPrint={onPrint}
          resendStatus={resend}
          usesPhysicalKeys={usesPhysicalKeys}
        />
      ) : (
        <CascadeProgress
          current={cascade.currentStep}
          usesPhysicalKeys={usesPhysicalKeys}
          payLater={payment === 'later'}
        />
      )}

      <SummaryRows
        roomCode={room.code}
        modeLabel={formatModeDynamic(mode, dynamicPricing)}
        start={start}
        end={end}
        guestLabel={isAnonymous ? (anonymousId ?? 'Ospite anonimo') : guest.fullName?.trim() || '—'}
        payment={payment}
        total={total}
      />

      {/* Compat hidden buttons per test E2E pre-existenti */}
      <button
        type="button"
        data-testid="walkin-confirm"
        onClick={() => void runWalkInCascade(wizard)}
        className="hidden"
      >
        CONFERMA & PAGA
      </button>
    </section>
  );
}

// --------------------------- WarningsBanner ---------------------------
// HM-355 cluster 16.1.7: banner rosso/giallo che elenca step graceful
// falliti durante cascade. Camera comunque OCCUPATA, booking salvato:
// l'operatore deve completare manualmente le azioni in elenco.

const STAGE_LABELS: Record<string, string> = {
  payment: 'Pagamento',
  fiscal: 'Scontrino fiscale',
  access_code: 'Codice di accesso',
  messaging: 'Invio WhatsApp',
  confirm: 'Conferma prenotazione',
  check_in: 'Check-in (apertura camera)',
};

function WarningsBanner({
  warnings,
}: {
  warnings: { stage: string; message: string }[];
}) {
  return (
    <div
      className="rounded-xl border bg-[#FFF8E5] px-4 py-3 flex flex-col gap-2"
      style={{ borderColor: '#F4D376' }}
      data-testid="cascade-warnings"
    >
      <p className="text-sm font-semibold text-text-primary">Check-in completato con avvisi</p>
      <p className="text-xs text-text-secondary">
        La camera e' OCCUPATA, ma alcuni step automatici sono falliti. Verificare manualmente:
      </p>
      <ul className="text-xs text-text-primary flex flex-col gap-1 mt-1">
        {warnings.map((w) => (
          <li key={`${w.stage}-${w.message}`} data-testid={`warning-${w.stage}`}>
            <span className="font-semibold">{STAGE_LABELS[w.stage] ?? w.stage}</span>
            <span className="text-text-secondary"> · {w.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------- SuccessView ---------------------------

function SuccessView({
  accessCode,
  roomCode,
  onResend,
  onPrint,
  resendStatus,
  usesPhysicalKeys,
}: {
  accessCode: string;
  roomCode: string;
  onResend: () => void;
  onPrint: () => void;
  resendStatus: { status: 'idle' | 'sending' | 'sent' | 'error'; msg?: string };
  usesPhysicalKeys: boolean;
}) {
  return (
    <div className="flex flex-col items-center text-center gap-4" data-testid="cascade-done">
      <span
        className="w-16 h-16 rounded-full flex items-center justify-center text-text-on-accent"
        style={{
          background: 'linear-gradient(135deg, #8BAE7C 0%, #5F8A50 100%)',
          boxShadow: '0 6px 20px rgba(95, 138, 80, 0.3)',
        }}
      >
        <CircleCheck size={36} />
      </span>
      <div>
        <h3 className="text-xl font-bold text-text-primary">Check-in completato</h3>
        <p className="text-sm text-text-secondary mt-1">
          {usesPhysicalKeys
            ? `Consegna la chiave fisica della camera ${roomCode} all'ospite`
            : `Codice di accesso generato per camera ${roomCode}`}
        </p>
      </div>

      {usesPhysicalKeys ? (
        <div
          className="w-full rounded-2xl px-6 py-7 flex flex-col items-center gap-3 mt-2"
          style={{
            background: 'linear-gradient(135deg, #FAF7F2 0%, white 100%)',
            border: '2px dashed #E8DFD1',
          }}
          data-testid="physical-key-box"
        >
          <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-muted">
            Chiave fisica
          </p>
          <p
            className="font-bold text-text-primary"
            style={{ fontSize: 44, lineHeight: 1.1 }}
            data-testid="walkin-room-code"
          >
            {roomCode}
          </p>
          <p className="text-xs text-text-secondary">
            Consegna la chiave camera <strong>{roomCode}</strong>. Ricordati di ritirarla al
            check-out.
          </p>
        </div>
      ) : (
        <div
          className="w-full rounded-2xl px-6 py-7 flex flex-col items-center gap-2 mt-2"
          style={{
            background: 'linear-gradient(135deg, #FAF7F2 0%, white 100%)',
            border: '2px dashed #E8DFD1',
          }}
          data-testid="access-code-box"
        >
          <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-muted">
            Codice di accesso
          </p>
          <p
            data-testid="walkin-access-code"
            className="font-mono font-bold text-text-primary"
            style={{ fontSize: 44, letterSpacing: 8, lineHeight: 1.1 }}
          >
            {formatCodeForDisplay(accessCode)}
          </p>
          <p className="text-xs text-text-secondary">
            Apre <strong>sbarra esterna</strong>, <strong>camera {roomCode}</strong> e{' '}
            <strong>garage</strong>
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 w-full mt-2" data-testid="action-row">
        <ActionButton
          icon={<MessageCircle size={18} />}
          label={resendStatus.status === 'sending' ? 'Invio…' : 'Ripeti WhatsApp'}
          onClick={onResend}
          disabled={resendStatus.status === 'sending' || usesPhysicalKeys}
          testId="walkin-resend-wa"
        />
        <ActionButton
          icon={<Printer size={18} />}
          label="Stampa promemoria"
          onClick={onPrint}
          testId="walkin-print"
        />
      </div>
      {resendStatus.status === 'sent' && (
        <p className="text-xs text-[#5F8A50] font-semibold">✓ WhatsApp inviato</p>
      )}
      {resendStatus.status === 'error' && resendStatus.msg && (
        <p className="text-xs text-red-600">{resendStatus.msg}</p>
      )}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border-input bg-bg-card text-sm font-semibold text-text-primary hover:bg-bg-base hover:border-accent-primary/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {icon}
      {label}
    </button>
  );
}

// --------------------------- CascadeProgress ---------------------------

function CascadeProgress({
  current,
  usesPhysicalKeys,
  payLater,
}: { current: string; usesPhysicalKeys: boolean; payLater: boolean }) {
  // Revisione pagamenti 2026-07-22: con "Paga all'uscita" gli step
  // payment/fiscal vengono saltati dalla cascade → non mostrarli proprio
  // nella progress (niente step "finti" che si completano da soli).
  const steps = payLater
    ? CASCADE_STEPS.filter((s) => s.key !== 'payment' && s.key !== 'fiscal')
    : CASCADE_STEPS;
  return (
    <div className="bg-bg-base rounded-xl p-5" data-testid="cascade-progress">
      <p className="text-sm font-semibold text-text-primary mb-3">Esecuzione check-in...</p>
      <ol className="flex flex-col gap-2.5">
        {steps.map((s, i) => {
          const state = progress(current, s.key);
          const label =
            s.key === 'access_code' && usesPhysicalKeys ? 'Preparazione chiave' : s.label;
          return (
            <li
              key={s.key}
              data-testid={`cascade-${s.key}`}
              data-state={state}
              className="flex items-center gap-3"
            >
              <span
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0',
                  state === 'pending' && 'bg-bg-card border border-border-base text-text-muted',
                  state === 'active' && 'bg-accent-primary text-text-on-accent animate-pulse',
                  state === 'done' && 'text-text-on-accent',
                )}
                style={state === 'done' ? { background: '#8BAE7C' } : undefined}
              >
                {state === 'done' ? <CircleCheck size={12} /> : i + 1}
              </span>
              <span
                className={cn(
                  'text-sm',
                  state === 'pending'
                    ? 'text-text-muted'
                    : state === 'active'
                      ? 'text-text-primary font-semibold'
                      : 'text-text-secondary',
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// --------------------------- SummaryRows ---------------------------

function SummaryRows({
  roomCode,
  modeLabel,
  start,
  end,
  guestLabel,
  payment,
  total,
}: {
  roomCode: string;
  modeLabel: string;
  start: Date;
  end: Date;
  guestLabel: string;
  payment: WalkInPayment;
  total: number;
}) {
  // Revisione pagamenti 2026-07-22: con "Paga all'uscita" il totale NON e'
  // stato incassato → riga dedicata "Da incassare al checkout" (arancione).
  const payLater = payment === 'later';
  const paymentLabel =
    payment === 'card' ? 'Carta' : payment === 'cash' ? 'Contanti' : `Paga all'uscita`;
  const rows = [
    { label: 'Camera', value: roomCode },
    // Se l'uscita cade in un ALTRO giorno (pernotto → 12:00 di domani),
    // dillo: "(22:10 → 12:00)" da solo sembra un orario che torna indietro.
    {
      label: 'Modalita',
      value: `${modeLabel} (${fmt(start)} → ${end.getDate() !== start.getDate() ? 'domani ' : ''}${fmt(end)})`,
    },
    { label: 'Ospite', value: guestLabel },
    { label: 'Pagamento', value: paymentLabel },
    {
      label: payLater ? 'Da incassare al checkout' : 'Totale',
      value: `EUR ${total.toFixed(2)}`,
      strong: true,
      accent: payLater,
      testId: payLater ? 'recap-unpaid-total' : undefined,
    },
  ];
  return (
    <div className="bg-bg-base rounded-xl p-4 flex flex-col gap-2" data-testid="summary-rows">
      {rows.map((r, i) => (
        <div
          key={r.label}
          className={cn(
            'flex items-center justify-between',
            i < rows.length - 1 && 'pb-2 border-b border-border-base/60',
          )}
        >
          <span
            className={cn(
              'text-xs uppercase tracking-[0.5px] font-semibold',
              r.accent ? 'text-[#A8453A]' : 'text-text-muted',
            )}
          >
            {r.label}
          </span>
          <span
            data-testid={r.testId}
            className={cn(
              'text-sm',
              r.strong ? 'font-bold text-base' : 'font-semibold',
              r.accent ? 'text-[#A8453A]' : 'text-text-primary',
            )}
          >
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// --------------------------- helpers ---------------------------

function progress(current: string, target: string): 'pending' | 'active' | 'done' {
  const order = ['booking', 'payment', 'fiscal', 'access_code', 'messaging', 'done'];
  const ci = order.indexOf(current);
  const ti = order.indexOf(target);
  if (ci < 0 || ti < 0) return 'pending';
  if (current === 'done') return 'done';
  if (ci > ti) return 'done';
  if (ci === ti) return 'active';
  return 'pending';
}

function fmt(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatCodeForDisplay(code: string): string {
  // 707070 -> "70 70 70" per leggibilita' a distanza dal monitor reception.
  return code.replace(/(\d{2})(?=\d)/g, '$1 ');
}
